/**
 * EDI Service - Provides parsing and metadata enrichment for EDI documents
 * Uses comprehensive X12 dictionary for element names and code translations
 */

/**
 * Parse raw EDI content
 * @param {string} ediContent - Raw EDI file content
 * @returns {object} Parsed EDI with segments and metadata
 */
export function parseEDI(ediContent) {
    try {
        return parseEDIContent(ediContent);
    } catch (error) {
        console.error('EDI parsing error:', error);
        return {
            success: false,
            error: error.message,
            segments: []
        };
    }
}

/**
 * Parse EDI content using local parser with comprehensive dictionary
 */
function parseEDIContent(ediContent) {
    const segments = [];
    const lines = ediContent.split(/[~\n]/).filter(line => line.trim());

    lines.forEach((line, idx) => {
        const parts = line.split('*');
        const segmentId = parts[0].trim();
        const elements = parts.slice(1);

        segments.push({
            segment_id: segmentId,
            segment_num: idx + 1,
            segment_name: getSegmentNameFromLibrary(segmentId),
            raw: line.trim(),
            elements: elements
                .map((val, elemIdx) => ({
                    position: elemIdx + 1,
                    value: val || '',
                    name: getElementNameFromLibrary(segmentId, elemIdx + 1),
                    description: getElementDescription(segmentId, elemIdx + 1),
                    display: translateCode(segmentId, elemIdx + 1, val)
                }))
                .filter(el => el.value !== '')
                .map(el => ({
                    ...el,
                    display: (() => {
                        const formatted = formatValue(segmentId, el.position, el.value);
                        if (formatted !== el.value) {
                            return `${formatted} (${el.value})`;
                        }
                        return translateCode(segmentId, el.position, el.value);
                    })()
                })),
            loop_context: getLoopContext(segmentId)
        });
    });

    // Extract basic metadata from ISA segment
    const isaSegment = segments.find(s => s.segment_id === 'ISA');
    const gsSegment = segments.find(s => s.segment_id === 'GS');

    return {
        success: true,
        interchange: null,
        segments: segments,
        metadata: {
            interchangeControlNumber: isaSegment?.elements[12]?.value || '',
            interchangeDate: isaSegment?.elements[8]?.value || '',
            senderId: isaSegment?.elements[5]?.value || '',
            receiverId: isaSegment?.elements[7]?.value || '',
            testIndicator: isaSegment?.elements[14]?.value || '',
            transactionType: gsSegment?.elements[0]?.value || 'Unknown',
            functionalGroupCount: 1,
            transactionCount: 1,
            _parser: 'fallback'
        }
    };
}


/**
 * Get segment name from x12-parser metadata or fallback to local definitions
 */
export function getSegmentNameFromLibrary(segmentId) {
    // x12-parser may expose segment names, fallback to local definitions
    return SEGMENT_NAMES[segmentId] || segmentId;
}

/**
 * Get element name from local X12 dictionary
 * This supplements what x12-parser provides
 */
export function getElementNameFromLibrary(segmentId, position) {
    const names = ELEMENT_NAMES[segmentId];
    if (names && names[position]) {
        return names[position];
    }
    return `Element ${position}`;
}

/**
 * Get element description (more detailed than name)
 */
function getElementDescription(segmentId, position) {
    const descriptions = ELEMENT_DESCRIPTIONS[segmentId];
    return descriptions?.[position] || null;
}

/**
 * Translate code values to human-readable descriptions
 */
export function translateCode(segmentId, position, value) {
    if (!value) return value;

    const translations = CODE_TRANSLATIONS[segmentId]?.[position];
    if (translations && translations[value]) {
        return `${value} - ${translations[value]}`;
    }
    return value;
}

/**
 * Check if the element at this position is a date field
 */
function isDateField(segmentId, position) {
    const dateFields = {
        'ISA': [9],
        'GS': [4],
        'BEG': [5],
        'DTM': [2],
        'BIG': [1],
        'BAK': [4],
        'BSN': [3],
        'DTP': [3],
        'FST': [4],
        'SHP': [2, 4, 6],
        'G93': [3, 4],
        'ITD': [5, 6],
        'FOB': [6]
    };
    return dateFields[segmentId]?.includes(position) || false;
}

/**
 * Check if the element at this position is a time field
 */
function isTimeField(segmentId, position) {
    const timeFields = {
        'ISA': [10],
        'GS': [5],
        'DTM': [3],
        'BSN': [4],
        'DTP': [4],
        'FST': [5],
        'SHP': [3, 5, 7]
    };
    return timeFields[segmentId]?.includes(position) || false;
}

/**
 * Check if the element at this position is a currency/amount field
 */
function isCurrencyField(segmentId, position) {
    const currencyFields = {
        'AMT': [2],
        'SAC': [5, 6, 7, 8],
        'TDS': [1, 2, 3, 4],
        'CLM': [2],
        'CTP': [3, 4],
        'IT1': [4, 5],
        'PO1': [4, 5],
        'TXI': [2, 3],
        'CAD': [4, 5],
        'CLP': [3, 4],
        'SVC': [2, 3, 4, 5, 6, 7],
        'CAS': [4, 5, 7, 8, 10, 11],
        'PLB': [4, 6, 8, 10, 12, 14],
        'RMR': [3, 4],
        'ADX': [1],
        'CN1': [2],
        'HCP': [2, 3],
        'SV1': [2],
        'SV2': [3],
        'SV5': [4],
        'PS1': [2],
        'CSH': [3]
    };
    return currencyFields[segmentId]?.includes(position) || false;
}

/**
 * Check if the element at this position is a phone field
 */
function isPhoneField(segmentId, position) {
    const phoneFields = {
        'PER': [4, 6, 8]  // PER-04, PER-06, PER-08 (with PER-03/05/07 = TE/FX)
    };
    return phoneFields[segmentId]?.includes(position) || false;
}

/**
 * Check if the element at this position is a postal code field
 */
function isPostalCodeField(segmentId, position) {
    const postalCodeFields = {
        'N4': [3]
    };
    return postalCodeFields[segmentId]?.includes(position) || false;
}

/**
 * Check if the element at this position is a boolean field (Y/N)
 */
function isBooleanField(segmentId, position) {
    const booleanFields = {
        'ISA': [14],      // Acknowledgment Requested
        'GS': [8],        // Responsible Agency Code indicator context
        'PO1': [9],       // Basis of Unit Price Code sometimes boolean-ish
        'ITD': [2],       // Terms Type Code sometimes boolean-ish
        'ACK': [2],       // Product/Service ID Condition Code
        'BAK': [1],       // Transaction Set Purpose Code (change indicator)
        'INS': [1],       // Yes/No Condition Response Code
        'SBR': [5]        // Employment Status Code
    };
    return booleanFields[segmentId]?.includes(position) || false;
}

/**
 * Check if the element at this position is a weight field
 */
function isWeightField(segmentId, position) {
    const weightFields = {
        'TD1': [6, 8],    // Weight
        'TD3': [5],       // Weight
        'TD4': [3],       // sometimes weight related
        'ISS': [3],       // Weight
        'CR1': [2],       // Weight
        'PO1': [6],       // Weight
        'IT1': [8]        // Weight
    };
    return weightFields[segmentId]?.includes(position) || false;
}

/**
 * Format date value (YYMMDD or YYYYMMDD to MM/DD/YYYY)
 */
function formatDate(value) {
    if (!value) return value;

    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');

    if (digits.length === 6) {
        // YYMMDD format
        const yy = digits.substring(0, 2);
        const mm = digits.substring(2, 4);
        const dd = digits.substring(4, 6);
        // Assume 20xx for years, adjust if needed for older dates
        const fullYear = parseInt(yy) >= 50 ? `19${yy}` : `20${yy}`;
        return `${mm}/${dd}/${fullYear}`;
    } else if (digits.length === 8) {
        // YYYYMMDD format
        const yyyy = digits.substring(0, 4);
        const mm = digits.substring(4, 6);
        const dd = digits.substring(6, 8);
        return `${mm}/${dd}/${yyyy}`;
    }

    return value;
}

/**
 * Format time value (HHMM to HH:MM)
 */
function formatTime(value) {
    if (!value) return value;

    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');

    if (digits.length === 4) {
        const hh = digits.substring(0, 2);
        const mm = digits.substring(2, 4);
        return `${hh}:${mm}`;
    }

    return value;
}

/**
 * Format currency value - adds $ and commas (e.g., "1234.50" → "$1,234.50")
 */
function formatCurrency(value) {
    if (!value) return value;

    // Remove any non-digit/non-decimal characters
    const cleanValue = value.replace(/[^\d.]/g, '');
    const num = parseFloat(cleanValue);

    if (isNaN(num)) return value;

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(num);
}

/**
 * Format phone number as (XXX) XXX-XXXX
 */
function formatPhone(value) {
    if (!value) return value;

    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');

    if (digits.length === 10) {
        return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
    }

    return value;
}

/**
 * Format ZIP+4 as 12345-6789
 */
function formatPostalCode(value) {
    if (!value) return value;

    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');

    if (digits.length === 9) {
        return `${digits.substring(0, 5)}-${digits.substring(5, 9)}`;
    } else if (digits.length === 5) {
        return digits;
    }

    return value;
}

/**
 * Format boolean Y/N to Yes/No
 */
function formatBoolean(value) {
    if (!value) return value;

    const upperValue = value.toString().toUpperCase();

    if (upperValue === 'Y' || upperValue === 'YES' || upperValue === 'TRUE' || upperValue === '1') {
        return 'Yes';
    } else if (upperValue === 'N' || upperValue === 'NO' || upperValue === 'FALSE' || upperValue === '0') {
        return 'No';
    }

    return value;
}

/**
 * Format weight with lb/kg suffix
 */
function formatWeight(value, unit) {
    if (!value) return value;

    // Remove any non-digit/non-decimal characters
    const cleanValue = value.replace(/[^\d.]/g, '');
    const num = parseFloat(cleanValue);

    if (isNaN(num)) return value;

    // Format with commas
    const formatted = new Intl.NumberFormat('en-US').format(num);

    // Add unit suffix
    if (unit) {
        const unitUpper = unit.toUpperCase();
        if (unitUpper === 'KG' || unitUpper === 'KGM') {
            return `${formatted} kg`;
        } else if (unitUpper === 'LB' || unitUpper === 'LBS') {
            return `${formatted} lb`;
        }
    }

    return `${formatted}`;
}

/**
 * Main formatting function - applies date/time/currency formatting if applicable
 */
export function formatValue(segmentId, position, value, context = {}) {
    if (!value) return value;

    if (isDateField(segmentId, position)) {
        return formatDate(value);
    }

    if (isTimeField(segmentId, position)) {
        return formatTime(value);
    }

    if (isCurrencyField(segmentId, position)) {
        return formatCurrency(value);
    }

    if (isPhoneField(segmentId, position)) {
        return formatPhone(value);
    }

    if (isPostalCodeField(segmentId, position)) {
        return formatPostalCode(value);
    }

    if (isBooleanField(segmentId, position)) {
        return formatBoolean(value);
    }

    if (isWeightField(segmentId, position)) {
        // Try to get unit from context or nearby elements
        const unit = context?.unit || context?.elements?.find(e =>
            e.position === position - 1)?.value;
        return formatWeight(value, unit);
    }

    return value;
}

// ============================================================================
// X12 Element Definitions (supplements x12-parser)
// These provide human-readable names for all EDI elements
// ============================================================================

const SEGMENT_NAMES = {
    'ISA': 'Interchange Control Header',
    'IEA': 'Interchange Control Trailer',
    'GS': 'Functional Group Header',
    'GE': 'Functional Group Trailer',
    'ST': 'Transaction Set Header',
    'SE': 'Transaction Set Trailer',
    'BEG': 'Beginning Segment for Purchase Order',
    'CUR': 'Currency',
    'REF': 'Reference Information',
    'PER': 'Administrative Contact',
    'N1': 'Party Identification',
    'N2': 'Additional Name Information',
    'N3': 'Address Information',
    'N4': 'Geographic Location',
    'PO1': 'Purchase Order Line Item',
    'PID': 'Product/Item Description',
    'CTT': 'Transaction Totals',
    'AMT': 'Monetary Amount',
    'TD5': 'Carrier Details',
    'SAC': 'Service/Allowance/Charge',
    'ITD': 'Terms of Sale',
    'DTM': 'Date/Time Reference',
    'FOB': 'F.O.B. Related Instructions',
    'MSG': 'Message Text',
    'BIG': 'Beginning Segment for Invoice',
    'IT1': 'Baseline Item Data (Invoice)',
    'TDS': 'Total Monetary Value Summary',
    'BAK': 'Beginning Segment for PO Acknowledgment',
    'ACK': 'Line Item Acknowledgment',
    'BSN': 'Beginning Segment for Ship Notice',
    'HL': 'Hierarchical Level',
    'PRF': 'Purchase Order Reference',
    'SN1': 'Item Detail (Shipment)',
    'LIN': 'Item Identification',
    'MAN': 'Marks and Numbers',
    'TD1': 'Carrier Details (Quantity/Weight)',
    'CAD': 'Carrier Detail',
    'TXI': 'Tax Information',
    // 820 Payment Order/Remittance Advice
    'RAH': 'Report Beginning',
    'NTE': 'Note/Special Instruction',
    'TRN': 'Trace',
    'RMR': 'Remittance Advice Accounts Receivable',
    'ADX': 'Adjustment',
    // 824 Application Advice
    'OTI': 'Original Transaction Identification',
    'TED': 'Technical Error Description',
    // 830 Planning Schedule
    'BFR': 'Beginning Segment for Planning Schedule',
    'ATH': 'Resource Authorization',
    'FST': 'Forecast Schedule',
    'SHP': 'Shipped/Received Information',
    // 832 Price/Sales Catalog
    'CTA': 'Contract Type',
    'G93': 'Price Bracket',
    'CF1': 'Conversion Factor',
    // 834 Benefit Enrollment
    'BGN': 'Beginning Segment',
    'INS': 'Insured Benefit',
    'DTP': 'Date or Time or Period',
    'NM1': 'Individual or Organizational Name',
    'DMG': 'Demographic Information',
    'HD': 'Health Coverage',
    'LX': 'Assigned Number',
    // 835 Health Care Claim Payment
    'CLP': 'Claim Level Data',
    'CAS': 'Claims Adjustment',
    'SVC': 'Service Payment Information',
    'LQ': 'Industry Code Identification',
    'PLB': 'Provider Level Adjustment',
    // 837 Health Care Claim
    'BHT': 'Beginning of Hierarchical Transaction',
    'PRV': 'Provider Information',
    'SBR': 'Subscriber Information',
    'PAT': 'Patient Information',
    'CLM': 'Health Claim',
    'PWK': 'Paperwork',
    'CN1': 'Contract Information',
    'HCP': 'Health Care Pricing',
    'CR1': 'Ambulance Transport Information',
    'HI': 'Health Care Information Codes',
    'SV1': 'Professional Service',
    'SV2': 'Institutional Service',
    'SV5': 'Durable Medical Equipment Service',
    'PS1': 'Purchased Service Information',
    // 856 Additional
    'TD3': 'Carrier Details (Equipment)',
    'TD4': 'Carrier Details (Special Handling)',
    'QTY': 'Quantity',
    // 810 Additional
    'ISS': 'Invoice Shipment Summary',
    // 850 Additional
    'CSH': 'Sales Requirements',
    'TC2': 'Commodity',
    'CTP': 'Pricing Information',
    'IT8': 'Conditions of Sale',
    'MEA': 'Measurements',
    'PKG': 'Packaging'
};

const ELEMENT_NAMES = {
    'ISA': {
        1: 'Auth Info Qualifier',
        2: 'Auth Information',
        3: 'Security Info Qualifier',
        4: 'Security Information',
        5: 'Interchange ID Qualifier',
        6: 'Interchange Sender ID',
        7: 'Interchange ID Qualifier',
        8: 'Interchange Receiver ID',
        9: 'Interchange Date',
        10: 'Interchange Time',
        11: 'Control Standards ID',
        12: 'Control Version Number',
        13: 'Interchange Control Number',
        14: 'Acknowledgment Requested',
        15: 'Usage Indicator',
        16: 'Subelement Separator'
    },
    'IEA': {
        1: 'Number of Included Groups',
        2: 'Interchange Control Number'
    },
    'GS': {
        1: 'Functional ID Code',
        2: 'Application Sender Code',
        3: 'Application Receiver Code',
        4: 'Date',
        5: 'Time',
        6: 'Group Control Number',
        7: 'Responsible Agency Code',
        8: 'Version/Release/Industry ID'
    },
    'GE': {
        1: 'Number of Transaction Sets',
        2: 'Group Control Number'
    },
    'ST': {
        1: 'Transaction Set ID Code',
        2: 'Transaction Set Control Number'
    },
    'SE': {
        1: 'Number of Included Segments',
        2: 'Transaction Set Control Number'
    },
    'BEG': {
        1: 'Transaction Set Purpose',
        2: 'Purchase Order Type',
        3: 'Purchase Order Number',
        4: 'Release Number',
        5: 'Purchase Order Date'
    },
    'CUR': {
        1: 'Entity Identifier Code',
        2: 'Currency Code'
    },
    'REF': {
        1: 'Reference Identification Qualifier',
        2: 'Reference Identification'
    },
    'PER': {
        1: 'Contact Function Code',
        2: 'Name',
        3: 'Communication Number Qualifier',
        4: 'Communication Number'
    },
    'N1': {
        1: 'Entity Identifier Code',
        2: 'Name',
        3: 'Identification Code Qualifier',
        4: 'Identification Code'
    },
    'N2': {
        1: 'Name'
    },
    'N3': {
        1: 'Address Information',
        2: 'Address Information'
    },
    'N4': {
        1: 'City Name',
        2: 'State or Province Code',
        3: 'Postal Code',
        4: 'Country Code'
    },
    'PO1': {
        1: 'Assigned Identification',
        2: 'Quantity',
        3: 'Unit or Basis for Measurement',
        4: 'Unit Price',
        5: 'Basis of Unit Price Code',
        6: 'Product/Service ID Qualifier',
        7: 'Product/Service ID'
    },
    'PID': {
        1: 'Item Description Type',
        2: 'Product/Process Characteristic Code',
        3: 'Agency Qualifier Code',
        4: 'Product Description Code',
        5: 'Description'
    },
    'CTT': {
        1: 'Number of Line Items',
        2: 'Hash Total'
    },
    'TD5': {
        1: 'Routing Sequence Code',
        2: 'Identification Qualifier',
        3: 'Identification Code',
        4: 'Transportation Method/Type',
        5: 'Routing'
    },
    'SAC': {
        1: 'Allowance or Charge Indicator',
        2: 'Service/Promotion/Allowance/Charge Code',
        3: 'Agency Qualifier Code',
        4: 'Agency Service/Promotion/Allowance/Charge Code',
        5: 'Amount'
    },
    'DTM': {
        1: 'Date/Time Qualifier',
        2: 'Date',
        3: 'Time',
        4: 'Time Code'
    },
    'BIG': {
        1: 'Invoice Date',
        2: 'Invoice Number',
        3: 'Purchase Order Date',
        4: 'Purchase Order Number',
        5: 'Release Number'
    },
    'IT1': {
        1: 'Assigned Identification',
        2: 'Quantity Invoiced',
        3: 'Unit or Basis for Measurement',
        4: 'Unit Price',
        5: 'Basis of Unit Price Code',
        6: 'Product/Service ID Qualifier',
        7: 'Product/Service ID'
    },
    'TDS': {
        1: 'Amount'
    },
    'BAK': {
        1: 'Transaction Set Purpose Code',
        2: 'Acknowledgment Type',
        3: 'Purchase Order Number',
        4: 'Date'
    },
    'ACK': {
        1: 'Line Item Status Code',
        2: 'Quantity',
        3: 'Unit or Basis for Measurement',
        4: 'Date/Time Qualifier',
        5: 'Date'
    },
    'BSN': {
        1: 'Transaction Set Purpose Code',
        2: 'Shipment Identification',
        3: 'Date',
        4: 'Time'
    },
    'HL': {
        1: 'Hierarchical ID Number',
        2: 'Hierarchical Parent ID Number',
        3: 'Hierarchical Level Code'
    },
    'SN1': {
        1: 'Assigned Identification',
        2: 'Number of Units Shipped',
        3: 'Unit or Basis for Measurement'
    },
    'LIN': {
        1: 'Assigned Identification',
        2: 'Product/Service ID Qualifier',
        3: 'Product/Service ID'
    },
    'MAN': {
        1: 'Marks and Numbers Qualifier',
        2: 'Marks and Numbers'
    },
    'PRF': {
        1: 'Purchase Order Number',
        2: 'Release Number',
        3: 'Purchase Order Date'
    },
    'TXI': {
        1: 'Tax Type Code',
        2: 'Monetary Amount',
        3: 'Percent',
        4: 'Tax Jurisdiction Code Qualifier',
        5: 'Tax Jurisdiction Code'
    },
    'CAD': {
        1: 'Transportation Method/Type Code',
        2: 'Equipment Initial',
        3: 'Equipment Number',
        4: 'Standard Carrier Alpha Code',
        5: 'Routing'
    },
    // 820 Payment Order/Remittance Advice
    'RAH': {
        1: 'Report Type Code',
        2: 'Reference Identification',
        3: 'Date'
    },
    'NTE': {
        1: 'Note Reference Code',
        2: 'Description'
    },
    'TRN': {
        1: 'Trace Type Code',
        2: 'Reference Identification',
        3: 'Originating Company Identifier',
        4: 'Reference Identification'
    },
    'RMR': {
        1: 'Reference Identification Qualifier',
        2: 'Reference Identification',
        3: 'Payment Action Code',
        4: 'Monetary Amount',
        5: 'Monetary Amount',
        6: 'Monetary Amount'
    },
    'ADX': {
        1: 'Monetary Amount',
        2: 'Adjustment Reason Code',
        3: 'Reference Identification Qualifier',
        4: 'Reference Identification'
    },
    // 824 Application Advice
    'OTI': {
        1: 'Application Acknowledgment Code',
        2: 'Reference Identification Qualifier',
        3: 'Reference Identification',
        4: 'Date',
        5: 'Time',
        6: 'Group Control Number'
    },
    'TED': {
        1: 'Application Error Condition Code',
        2: 'Data Element Reference Number',
        3: 'Copy of Bad Data Element',
        4: 'Data Element New Content'
    },
    // 830 Planning Schedule
    'BFR': {
        1: 'Transaction Set Purpose Code',
        2: 'Reference Identification',
        3: 'Release Number',
        4: 'Schedule Type Qualifier',
        5: 'Schedule Quantity Qualifier',
        6: 'Date',
        7: 'Date',
        8: 'Date',
        9: 'Date',
        10: 'Date'
    },
    'ATH': {
        1: 'Resource Authorization Code',
        2: 'Date',
        3: 'Date',
        4: 'Quantity',
        5: 'Quantity'
    },
    'FST': {
        1: 'Quantity',
        2: 'Forecast Qualifier',
        3: 'Timing Qualifier',
        4: 'Date',
        5: 'Date',
        6: 'Date'
    },
    'SHP': {
        1: 'Quantity Qualifier',
        2: 'Quantity',
        3: 'Date/Time Qualifier',
        4: 'Date',
        5: 'Time'
    },
    // 832 Price/Sales Catalog
    'CTA': {
        1: 'Contract Type Code',
        2: 'Purchase Order Type Code',
        3: 'Reference Identification'
    },
    'G93': {
        1: 'Price Bracket Identifier',
        2: 'Quantity',
        3: 'Unit or Basis for Measurement Code',
        4: 'Free-form Description'
    },
    'CF1': {
        1: 'Price Group Code',
        2: 'Price Group Description'
    },
    // 834 Benefit Enrollment
    'BGN': {
        1: 'Transaction Set Purpose Code',
        2: 'Reference Identification',
        3: 'Date',
        4: 'Time',
        5: 'Time Zone Code',
        6: 'Original Transaction Set Reference Number'
    },
    'INS': {
        1: 'Insured Indicator',
        2: 'Individual Relationship Code',
        3: 'Maintenance Type Code',
        4: 'Maintenance Reason Code',
        5: 'Benefit Status Code',
        6: 'Medicare Status Code'
    },
    'DTP': {
        1: 'Date/Time Qualifier',
        2: 'Date Time Period Format Qualifier',
        3: 'Date Time Period'
    },
    'NM1': {
        1: 'Entity Identifier Code',
        2: 'Entity Type Qualifier',
        3: 'Name Last or Organization Name',
        4: 'Name First',
        5: 'Name Middle',
        6: 'Name Prefix',
        7: 'Name Suffix',
        8: 'Identification Code Qualifier',
        9: 'Identification Code'
    },
    'DMG': {
        1: 'Date Time Period Format Qualifier',
        2: 'Date Time Period',
        3: 'Gender Code',
        4: 'Marital Status Code',
        5: 'Race or Ethnicity Code'
    },
    'HD': {
        1: 'Maintenance Type Code',
        2: 'Maintenance Reason Code',
        3: 'Insurance Line Code',
        4: 'Plan Coverage Description'
    },
    'LX': {
        1: 'Assigned Number'
    },
    // 835 Health Care Claim Payment
    'CLP': {
        1: 'Claim Submitter Identifier',
        2: 'Claim Status Code',
        3: 'Monetary Amount',
        4: 'Monetary Amount',
        5: 'Monetary Amount',
        6: 'Claim Filing Indicator Code'
    },
    'CAS': {
        1: 'Claim Adjustment Group Code',
        2: 'Claim Adjustment Reason Code',
        3: 'Monetary Amount',
        4: 'Quantity',
        5: 'Claim Adjustment Reason Code',
        6: 'Monetary Amount',
        7: 'Quantity'
    },
    'SVC': {
        1: 'Composite Medical Procedure Identifier',
        2: 'Monetary Amount',
        3: 'Monetary Amount',
        4: 'Description',
        5: 'Quantity',
        6: 'Composite Diagnosis Code Pointer'
    },
    'LQ': {
        1: 'Code List Qualifier Code',
        2: 'Industry Code'
    },
    'PLB': {
        1: 'Provider Identifier',
        2: 'Fiscal Period Date',
        3: 'Adjustment Reason Code',
        4: 'Monetary Amount'
    },
    // 837 Health Care Claim
    'BHT': {
        1: 'Hierarchical Structure Code',
        2: 'Transaction Set Purpose Code',
        3: 'Reference Identification',
        4: 'Date',
        5: 'Time',
        6: 'Transaction Type Code'
    },
    'PRV': {
        1: 'Provider Code',
        2: 'Reference Identification Qualifier',
        3: 'Reference Identification'
    },
    'SBR': {
        1: 'Payer Responsibility Sequence Number Code',
        2: 'Individual Relationship Code',
        3: 'Reference Identification',
        4: 'Name',
        5: 'Insurance Type Code'
    },
    'PAT': {
        1: 'Individual Relationship Code',
        2: 'Patient Location Code',
        3: 'Employment Status Code',
        4: 'Student Status Code'
    },
    'CLM': {
        1: 'Claim Submitter Identifier',
        2: 'Monetary Amount',
        3: 'Claim Filing Indicator Code',
        4: 'Non-Institutional Claim Type Code',
        5: 'Reference Identification'
    },
    'PWK': {
        1: 'Report Type Code',
        2: 'Report Transmission Code',
        3: 'Report Copies Needed',
        4: 'Entity Identifier Code'
    },
    'CN1': {
        1: 'Contract Type Code',
        2: 'Monetary Amount',
        3: 'Percent',
        4: 'Reference Identification'
    },
    'HCP': {
        1: 'Pricing Methodology',
        2: 'Monetary Amount',
        3: 'Monetary Amount',
        4: 'Reference Identification'
    },
    'CR1': {
        1: 'Unit or Basis for Measurement Code',
        2: 'Weight',
        3: 'Ambulance Transport Code',
        4: 'Ambulance Transport Reason Code'
    },
    'HI': {
        1: 'Health Care Code Information'
    },
    'SV1': {
        1: 'Composite Medical Procedure Identifier',
        2: 'Monetary Amount',
        3: 'Unit or Basis for Measurement Code',
        4: 'Quantity',
        5: 'Facility Code Value',
        6: 'Service Type Code'
    },
    'SV2': {
        1: 'Service Line Revenue Code',
        2: 'Composite Medical Procedure Identifier',
        3: 'Monetary Amount',
        4: 'Unit or Basis for Measurement Code',
        5: 'Quantity'
    },
    'SV5': {
        1: 'Composite Medical Procedure Identifier',
        2: 'Unit or Basis for Measurement Code',
        3: 'Quantity',
        4: 'Monetary Amount',
        5: 'Frequency Code'
    },
    'PS1': {
        1: 'Reference Identification',
        2: 'Monetary Amount'
    },
    // 856 Additional Segments
    'TD3': {
        1: 'Equipment Description Code',
        2: 'Equipment Initial',
        3: 'Equipment Number',
        4: 'Weight Qualifier',
        5: 'Weight'
    },
    'TD4': {
        1: 'Special Handling Code',
        2: 'Hazardous Material Code Qualifier',
        3: 'Hazardous Material Class Code',
        4: 'Description'
    },
    // 810 Additional Segments
    'ISS': {
        1: 'Number of Units Shipped',
        2: 'Unit or Basis for Measurement',
        3: 'Weight',
        4: 'Weight Unit Code'
    },
    // 850 Additional Segments
    'CSH': {
        1: 'Sales Requirement Code',
        2: 'Action Code',
        3: 'Monetary Amount'
    },
    'TC2': {
        1: 'Commodity Code Qualifier',
        2: 'Commodity Code'
    },
    'CTP': {
        1: 'Class of Trade Code',
        2: 'Price Identifier Code',
        3: 'Unit Price',
        4: 'Quantity',
        5: 'Unit of Measure'
    },
    'IT8': {
        1: 'Sales Requirement Code'
    },
    // Additional common segments
    'AMT': {
        1: 'Amount Qualifier Code',
        2: 'Monetary Amount'
    },
    'QTY': {
        1: 'Quantity Qualifier',
        2: 'Quantity'
    },
    'MEA': {
        1: 'Measurement Reference ID Code',
        2: 'Measurement Qualifier',
        3: 'Measurement Value'
    },
    'TD1': {
        1: 'Packaging Code',
        2: 'Lading Quantity'
    }
};

// More detailed descriptions for tooltips/hover
const ELEMENT_DESCRIPTIONS = {
    'ISA': {
        1: 'Code to identify the type of information in the Authorization Information field',
        5: 'Qualifier to identify the sender of the interchange',
        6: 'Identification code of the sender',
        15: 'P = Production, T = Test'
    },
    'GS': {
        1: 'Code identifying a group of application related transaction sets (e.g., PO = Purchase Order)'
    },
    'BEG': {
        1: '00 = Original, 01 = Cancellation, 05 = Replace',
        2: 'NE = New Order, DS = Drop Ship'
    },
    'N1': {
        1: 'ST = Ship To, BT = Bill To, BY = Buying Party, SE = Selling Party, VN = Vendor'
    }
};

// Code translations for common values
const CODE_TRANSLATIONS = {
    'ISA': {
        1: { '00': 'No Authorization', '01': 'UCS Communication ID' },
        3: { '00': 'No Security Information', '01': 'Password' },
        5: { '01': 'DUNS', '02': 'SCAC', '08': 'UCC EDI', '12': 'Phone', '14': 'DUNS+4', '30': 'US Federal Tax ID', 'ZZ': 'Mutually Defined' },
        15: { 'P': 'Production', 'T': 'Test' }
    },
    'GS': {
        1: { 'AG': 'Application Advice', 'FA': 'Functional Acknowledgment', 'HP': 'Health Care Claim Payment', 'IB': 'Invoice', 'IN': 'Invoice Information', 'PC': 'Purchase Order Change', 'PO': 'Purchase Order', 'PR': 'Purchase Order Acknowledgment', 'RA': 'Payment Order', 'RC': 'Receiving Advice', 'SH': 'Ship Notice/Manifest', 'SW': 'Warehouse Shipping Order', 'TA': 'Transportation Acknowledgment' }
    },
    'BEG': {
        1: { '00': 'Original', '01': 'Cancellation', '05': 'Replace' },
        2: { 'NE': 'New Order', 'DS': 'Drop Ship', 'SA': 'Stand-Alone', 'BK': 'Blanket' }
    },
    'CUR': {
        1: { 'BY': 'Buying Party', 'SE': 'Selling Party' }
    },
    'REF': {
        1: { 'BM': 'Bill of Lading', 'CR': 'Customer Reference', 'CT': 'Contract', 'DP': 'Department', 'IA': 'Internal Vendor', 'IX': 'Item Number', 'PD': 'Promotion', 'PO': 'Purchase Order', 'SN': 'Serial Number', 'TN': 'Transaction Reference', 'VP': 'Vendor Product', 'ZZ': 'Mutually Defined' }
    },
    'N1': {
        1: { 'ST': 'Ship To', 'BT': 'Bill To', 'BY': 'Buyer', 'SE': 'Seller', 'VN': 'Vendor', 'SH': 'Shipper', 'CA': 'Carrier', 'CN': 'Consignee', 'CU': 'Customer', 'SF': 'Ship From', 'SU': 'Supplier', 'WH': 'Warehouse' }
    },
    'TD5': {
        1: { 'B': 'Origin/Delivery Carrier', 'M': 'Motor', 'O': 'Origin Line-Haul', 'R': 'Rail', 'X': 'Interline' },
        4: { 'A': 'Air', 'CE': 'Coastal Waterway', 'D': 'Parcel Post', 'E': 'Expedited Truck', 'H': 'Customer Pickup', 'J': 'Motor', 'L': 'Contract Carrier', 'M': 'Motor (Common Carrier)', 'P': 'Private Carrier', 'Q': 'Ocean', 'R': 'Rail', 'T': 'Best Way', 'V': 'Vessel', 'W': 'Inland Waterway', 'X': 'Intermodal' }
    },
    'DTM': {
        1: { '001': 'Cancel After', '002': 'Delivery Requested', '003': 'Invoice', '004': 'Purchase Order', '007': 'Effective', '010': 'Requested Ship', '011': 'Shipped', '017': 'Estimated Delivery', '036': 'Expiration', '037': 'Ship Not Before', '038': 'Ship No Later', '050': 'Received', '150': 'Service Period Start', '151': 'Service Period End', '193': 'Period Start', '194': 'Period End', '472': 'Service' }
    },
    'ACK': {
        1: { 'IA': 'Item Accepted', 'IC': 'Item Changed', 'ID': 'Item Deleted', 'IE': 'Item Added', 'IF': 'Item Not Accepted' }
    },
    'BAK': {
        2: { 'AC': 'Acknowledge with Changes', 'AD': 'Acknowledge with Detail', 'AK': 'Acknowledge', 'AT': 'Accepted', 'NA': 'No Acknowledgment Needed', 'RD': 'Reject with Detail', 'RJ': 'Rejected' }
    },
    'BIG': {
        7: { 'DI': 'Debit Invoice', 'DR': 'Debit Memo', 'IN': 'Invoice', 'CN': 'Credit Invoice', 'CM': 'Credit Memo' }
    },
    'SAC': {
        1: { 'A': 'Allowance', 'C': 'Charge', 'N': 'No Allowance or Charge' },
        2: { 'D240': 'Discount', 'F050': 'Freight', 'H000': 'Handling', 'I190': 'Insurance' }
    },
    'HD': {
        1: { '001': 'Change', '021': 'Addition', '024': 'Cancellation' },
        3: { 'DENT': 'Dental', 'HLTH': 'Health', 'VISI': 'Vision', 'LIFE': 'Life' }
    },
    'INS': {
        1: { 'Y': 'Yes', 'N': 'No' },
        2: { '01': 'Spouse', '19': 'Child', '34': 'Other' },
        3: { '001': 'Change', '021': 'Addition', '024': 'Cancellation' }
    },
    'DMG': {
        3: { 'F': 'Female', 'M': 'Male', 'U': 'Unknown' }
    }
};

const LOOP_INFO = {
    'N1': { loop: 'N1', name: 'Party Loop' },
    'N2': { loop: 'N1', name: 'Party Loop' },
    'N3': { loop: 'N1', name: 'Party Loop' },
    'N4': { loop: 'N1', name: 'Party Loop' },
    'PER': { loop: 'N1', name: 'Party Loop' },
    'PO1': { loop: 'PO1', name: 'Line Item Loop' },
    'PID': { loop: 'PO1', name: 'Line Item Loop' },
    'SAC': { loop: 'PO1', name: 'Line Item Loop' },
    'IT1': { loop: 'IT1', name: 'Invoice Line Loop' },
    'HL': { loop: 'HL', name: 'Hierarchical Loop' },
    'LIN': { loop: 'HL', name: 'Hierarchical Loop' },
    'SN1': { loop: 'HL', name: 'Hierarchical Loop' }
};

function getLoopContext(segmentId) {
    return LOOP_INFO[segmentId] || null;
}

/**
 * Format EDI for display with rich metadata
 * Used by EDIPrettyViewer
 */
export function formatEDISegments(segments) {
    return segments.map(seg => ({
        ...seg,
        displayName: seg.segment_name || getSegmentNameFromLibrary(seg.segment_id),
        formattedElements: seg.elements.map(elem => ({
            ...elem,
            fullDisplay: elem.display || elem.value
        }))
    }));
}

/**
 * Get validation errors from parsed EDI
 */
export function validateEDI(interchange) {
    const errors = [];

    // Check segment counts match
    interchange.functionalGroups.forEach(group => {
        group.transactions.forEach(transaction => {
            const segments = transaction.segments;
            const stSegment = segments.find(s => s.id === 'ST');
            const seSegment = segments.find(s => s.id === 'SE');

            if (stSegment && seSegment) {
                const declaredCount = parseInt(seSegment.elements[0]?.value);
                const actualCount = segments.length;

                if (declaredCount !== actualCount) {
                    errors.push({
                        type: 'count_mismatch',
                        message: `SE segment declares ${declaredCount} segments but found ${actualCount}`,
                        segment: 'SE'
                    });
                }
            }
        });
    });

    return errors;
}

export default {
    parseEDI,
    formatEDISegments,
    validateEDI,
    formatValue
};
